import React, { useEffect, useState } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  InputNumber, 
  Checkbox, 
  Row, 
  Col, 
  message 
} from 'antd';
import { Student, studentAPI } from '../../services/studentAPI';
import { UserCompany, Company } from '../../services/companyAPI';
import dayjs from 'dayjs';

// Union tip yaradırıq
type SelectedCompany = UserCompany | Company;

interface StudentFormProps {
  visible: boolean;
  student: Student | null;
  onCancel: () => void;
  onSuccess: () => void;
  selectedCompany: SelectedCompany | null;
}

const StudentForm: React.FC<StudentFormProps> = ({ visible, student, onCancel, onSuccess, selectedCompany }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [finCodeChecking, setFinCodeChecking] = useState(false);
  const [idNumberChecking, setIdNumberChecking] = useState(false);

  useEffect(() => {
    if (visible) {
      if (student) {
        // Redaktə rejimi
        const formData = {
          ...student,
          birth_date: student.birth_date ? dayjs(student.birth_date) : null,
          // İmtahan paneli məlumatları
          exam_panel_access: student.exam_panel_access || false,
          exam_username: student.exam_username || undefined,
          // Şifrə boş saxlanır (təhlükəsizlik üçün), istifadəçi yenisini daxil edə bilər
          exam_password: undefined
        };
        form.setFieldsValue(formData);
      } else {
        // Yeni tələbə əlavə etmə rejimi
        form.resetFields();
        // Şirkəti avtomatik seç
        if (selectedCompany) {
          form.setFieldsValue({ 
            company: selectedCompany.id,
            student_status: 'aktiv',  // Default status
            exam_panel_access: false,
            exam_username: undefined,
            exam_password: undefined
          });
        }
      }
    }
  }, [visible, student, form, selectedCompany]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const formData = {
        ...values,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null,
      };

      let response: { success: boolean; data?: any; message?: string; errors?: any };
      if (student) {
        response = await studentAPI.updateStudent(student.id!, formData);
      } else {
        response = await studentAPI.createStudent(formData);
      }

      if (response.success) {
        message.success(response.message || (student ? 'Tələbə məlumatları yeniləndi' : 'Tələbə əlavə edildi'));
        onSuccess();
      } else {
        if (response.errors) {
          // Form xətalarını göstər
          const formErrors: any = {};
          Object.keys(response.errors).forEach(key => {
            formErrors[key] = {
              value: values[key],
              errors: Array.isArray(response.errors[key]) ? response.errors[key] : [response.errors[key]]
            };
          });
          form.setFields(Object.keys(formErrors).map(key => ({
            name: key,
            errors: formErrors[key].errors
          })));
          
          // Xüsusi xəta mesajlarını göstər
          if (response.errors.fin_code) {
            message.error(Array.isArray(response.errors.fin_code) ? response.errors.fin_code[0] : response.errors.fin_code);
          }
          if (response.errors.id_number) {
            message.error(Array.isArray(response.errors.id_number) ? response.errors.id_number[0] : response.errors.id_number);
          }
        } else {
          message.error(response.message || 'Xəta baş verdi');
        }
      }
    } catch (error) {
      message.error('Server xətası');
    } finally {
      setLoading(false);
    }
  };

  // FIN kod yoxlama funksiyası
  const checkFinCode = async (finCode: string) => {
    if (!finCode || finCode.length !== 7) return;
    if (student && student.fin_code === finCode) return; // Redaktə zamanı öz FIN kodunu yoxlama
    
    setFinCodeChecking(true);
    try {
      // API-dən yoxla
      const response = await studentAPI.getStudents({ 
        search: finCode,
        company: selectedCompany?.id 
      });
      
      // Eyni FIN kodlu tələbə varsa
      const existingStudent = response.data?.find((s: any) => 
        s.fin_code === finCode && s.id !== student?.id
      );
      
      if (existingStudent) {
        form.setFields([{
          name: 'fin_code',
          errors: [`Bu FIN kod artıq mövcuddur (Tələbə: ${existingStudent.full_name})`]
        }]);
      }
    } catch (error) {
      console.error('FIN kod yoxlama xətası:', error);
    } finally {
      setFinCodeChecking(false);
    }
  };

  // ID nömrəsi yoxlama funksiyası
  const checkIdNumber = async (idNumber: string) => {
    if (!idNumber || idNumber.length < 5) return;
    if (student && student.id_number === idNumber) return; // Redaktə zamanı öz ID-ni yoxlama
    
    setIdNumberChecking(true);
    try {
      // API-dən yoxla
      const response = await studentAPI.getStudents({ 
        search: idNumber,
        company: selectedCompany?.id 
      });
      
      // Eyni ID nömrəli tələbə varsa
      const existingStudent = response.data?.find((s: any) => 
        s.id_number === idNumber && s.id !== student?.id
      );
      
      if (existingStudent) {
        form.setFields([{
          name: 'id_number',
          errors: [`Bu vəsiqə nömrəsi artıq mövcuddur (Tələbə: ${existingStudent.full_name})`]
        }]);
      }
    } catch (error) {
      console.error('ID nömrəsi yoxlama xətası:', error);
    } finally {
      setIdNumberChecking(false);
    }
  };

  return (
    <Modal
      title={student ? 'Tələbə Məlumatlarını Redaktə Et' : 'Yeni Tələbə Əlavə Et'}
      open={visible}
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={loading}
      width={800}
      okText={student ? 'Yenilə' : 'Əlavə Et'}
      cancelText="Ləğv Et"
      okButtonProps={{ type: 'primary' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        scrollToFirstError
      >
        {/* Şirkət sahəsi (gizli) */}
        <Form.Item
          name="company"
          hidden
        >
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="first_name"
              label="Ad"
              rules={[{ required: true, message: 'Ad tələb olunur!' }]}
            >
              <Input placeholder="Tələbənin adı" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="last_name"
              label="Soyad"
              rules={[{ required: true, message: 'Soyad tələb olunur!' }]}
            >
              <Input placeholder="Tələbənin soyadı" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="father_name"
              label="Ata adı"
              rules={[{ required: true, message: 'Ata adı tələb olunur!' }]}
            >
              <Input placeholder="Ata adı" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="fin_code"
              label="FIN kodu"
              rules={[
                { required: true, message: 'FIN kodu tələb olunur!' },
                { pattern: /^[A-Z0-9]{7}$/, message: 'FIN kod 7 simvoldan ibarət olmalıdır (böyük hərflər və rəqəmlər)' }
              ]}
              hasFeedback
              validateStatus={finCodeChecking ? 'validating' : undefined}
            >
              <Input 
                placeholder="A1B2C3D" 
                maxLength={7} 
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  form.setFieldsValue({ fin_code: value });
                  // Xətaları təmizlə
                  form.setFields([{ name: 'fin_code', errors: [] }]);
                }}
                onBlur={(e) => {
                  const value = e.target.value.toUpperCase();
                  if (value.length === 7) {
                    checkFinCode(value);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="id_number"
              label="Vəsiqənin seriya nömrəsi"
              rules={[
                { required: true, message: 'Vəsiqə nömrəsi tələb olunur!' },
                { min: 5, message: 'Ən azı 5 simvol olmalıdır' },
                { max: 20, message: 'Maksimum 20 simvol ola bilər' }
              ]}
              hasFeedback
              validateStatus={idNumberChecking ? 'validating' : undefined}
            >
              <Input 
                placeholder="AA1234567, AZE55555, və s." 
                maxLength={20} 
                style={{ textTransform: 'uppercase' }}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  form.setFieldsValue({ id_number: value });
                  // Xətaları təmizlə
                  form.setFields([{ name: 'id_number', errors: [] }]);
                }}
                onBlur={(e) => {
                  const value = e.target.value.toUpperCase();
                  if (value.length >= 5) {
                    checkIdNumber(value);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="birth_date"
              label="Doğum tarixi"
              rules={[{ required: true, message: 'Doğum tarixi tələb olunur!' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                placeholder="Doğum tarixini seçin"
                format="DD.MM.YYYY"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="phone_number"
              label="Telefon nömrəsi"
              rules={[
                { pattern: /^(\+994|0)(50|51|55|70|77|99)[0-9]{7}$/, message: '+994501234567 və ya 0501234567 formatında olmalıdır' }
              ]}
            >
              <Input 
                placeholder="+994501234567 və ya 0501234567" 
                maxLength={15}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="student_status"
              label="Tələbə statusu"
              rules={[{ required: true, message: 'Status tələb olunur!' }]}
              tooltip="Yalnız aktiv tələbələr imtahan panelinə daxil ola bilər"
            >
              <Select 
                placeholder="Status seçin"
                options={[
                  { value: 'aktiv', label: 'Aktiv' },
                  { value: 'passiv', label: 'Passiv' },
                  { value: 'mezun', label: 'Mezun' },
                  { value: 'donmuş', label: 'Donmus' }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* İmtahan Paneli Hesabı */}
        <Row gutter={16} style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #f0f0f0' }}>
          <Col span={24}>
            <h4 style={{ marginBottom: 16, color: '#1890ff' }}>İmtahan Paneli Hesabı</h4>
            <p style={{ marginBottom: 16, color: '#666' }}>
              Tələbəyə imtahan panelinə giriş üçün istifadəçi adı və şifrə təyin edin.
            </p>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="exam_panel_access"
              valuePropName="checked"
            >
              <Checkbox>İmtahan paneli hesabı yarat</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => 
            prevValues.exam_panel_access !== currentValues.exam_panel_access
          }
        >
          {({ getFieldValue }) => {
            const createAccount = getFieldValue('exam_panel_access');
            return createAccount ? (
              <>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="exam_username"
                      label="İmtahan paneli istifadəçi adı"
                      rules={[
                        { required: true, message: 'İstifadəçi adı tələb olunur!' },
                        { min: 3, message: 'İstifadəçi adı ən azı 3 simvol olmalıdır!' }
                      ]}
                      extra="Bu istifadəçi adı ilə tələbə imtahan panelinə daxil ola biləcək"
                    >
                      <Input 
                        placeholder="İstifadəçi adı daxil edin (məsələn: telebe123)"
                        autoComplete="off"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="exam_password"
                      label="İmtahan paneli şifrəsi"
                      rules={[
                        { 
                          required: !student, 
                          message: 'Şifrə tələb olunur!' 
                        },
                        { 
                          min: 6, 
                          message: 'Şifrə ən azı 6 simvol olmalıdır!',
                          validateTrigger: 'onBlur'
                        }
                      ]}
                      extra={student ? "Boş saxlasanız köhnə şifrə qalacaq" : "Bu şifrə ilə tələbə imtahan panelinə daxil ola biləcək"}
                    >
                      <Input.Password 
                        placeholder={student ? "Yeni şifrə (boş saxlasanız köhnə şifrə qalacaq)" : "Şifrə daxil edin"}
                        autoComplete="new-password"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : null;
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default StudentForm;